<template>
  <form @submit.prevent="submit" autocomplete="off">
    <t-modal-section :title="$t('Policy Details')" class="mb-2">
      <b-row>
        <b-col sm="12" md="12" lg="6">
          <b-form-group
            :label="$t('Policy Name')"
            label-for="policy_name"
            :state="$validateState($v.form.name)"
          >
            <b-form-input
              autofocus
              data-test-id="modal-policy-policy-name"
              :state="$validateState($v.form.name)"
              id="policy_name"
              @blur="setValidationInput('name', $event.target.value)"
              :placeholder="$t('For example, Marketing')"
            />

            <template #invalid-feedback>
              <p
                v-if="
                  $v.form.name.$dirty &&
                  $v.form.name.required &&
                  !$v.form.name.minLength
                "
              >
                {{
                  $t("X must be at least X characters long.", {
                    field: $t("Policy"),
                    length: $v.form.name.$params.minLength.min,
                  })
                }}
              </p>
              <p v-if="$v.form.name.$dirty && !$v.form.name.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col sm="12" md="12" lg="6">
          <b-form-group
            :label="$t('Description')"
            label-for="description"
            :state="$validateState($v.form.description)"
          >
            <b-form-input
              :state="$validateState($v.form.description)"
              data-test-id="modal-policy-description"
              id="description"
              @blur="setValidationInput('description', $event.target.value)"
              :placeholder="$t('For example, Policy for all Marketing staff')"
            />

            <template #invalid-feedback>
              <p
                v-if="
                  $v.form.description.$dirty &&
                  $v.form.description.required &&
                  !$v.form.description.minLength
                "
              >
                {{
                  $t("X must be at least X characters long.", {
                    field: $t("Policy"),
                    length: $v.form.description.$params.minLength.min,
                  })
                }}
              </p>
              <p v-if="!$v.form.description.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
        </b-col>
      </b-row>
    </t-modal-section>
    <hr class="mb-5 mt-0" />
    <t-modal-section :title="$t('Content Filtering')">
      <p>
        {{
          $t(
            "Choose the level of filtering you want to apply to this policy (you can change this later)."
          )
        }}
      </p>
      <b-row>
        <b-col cols="12">
          <b-form-group>
            <t-radio-group v-model="form.level" :items="contentFiltering" />
          </b-form-group>
        </b-col>
      </b-row>
    </t-modal-section>
  </form>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from "vuex";
import { required, minLength } from "vuelidate/lib/validators";

import TModalSection from "@/components/TModalSection";

export default {
  components: {
    TModalSection,
  },
  data() {
    return {
      fetching: false,
      form: {
        name: null,
        description: null,
        level: "M",
      },
      contentFiltering: [
        {
          text: this.$t("Essential"),
          subtitle: this.$t("Essential security protection."),
          tooltip: this.$t(
            "Blocks these categories: Anonymizer, Compromised, Criminal Skills/Hacking, Hate Speech, Phishing/Fraud, Spam, Spyware and Malicious Sites."
          ),
          testID: "global-default-policy-low",
          value: "L",
          checked: false,
          icon: "shield",
        },
        {
          text: this.$t("Enhanced"),
          subtitle: this.$t(
            "Enhanced security protection, plus the blocking of adult related content."
          ),
          tooltip: this.$t(
            "Blocks Essential categories, plus Download Sites, Illegal Drugs, Online Ads, Pornography/Sex, Violence."
          ),
          testID: "global-default-policy-moderate",
          value: "M",
          checked: true,
          icon: "shield-shaded",
        },
        {
          text: this.$t("Optimum"),
          subtitle: this.$t(
            "Enhanced security protection, blocking of adult related content, plus blocking of productivity inhibiting sites."
          ),
          tooltip: this.$t(
            "Blocks Essential and Enhanced categories, plus Alcohol, Dating, Gambling, Nudity, Weapons."
          ),
          testID: "global-default-policy-high",
          value: "H",
          checked: false,
          icon: "shield-fill",
        },
      ],
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(5),
      },
      description: {
        required,
        minLength: minLength(5),
      },
      level: {},
    },
  },
  methods: {
    ...mapMutations("modal", ["userAction", "showModal"]),
    ...mapActions("customerPolicies", ["createCustomerPolicy"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.fetching) {
        this.fetching = true;
        try {
          const response = await this.createCustomerPolicy({
            id: this.selectedUser.id,
            body: this.form,
          });
          /* istanbul ignore else */
          if (response.code && response.code === 200) {
            this.showModal(false);
            let userRole = this.modalData.userRole ?? "customer";
            this.$router.push({
              path: `/${userRole}/content_filtering/policies/${response.id}`,
            });
          }
        } finally {
          this.fetching = false;
        }
      }
    },
  },
  computed: {
    ...mapGetters("modal", ["modalData"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  created() {
    this.userAction(this.submit);
  },
};
</script>
